import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { MapPin, Sparkles, Search, Mail, Sofa, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import UpgradeModal from '@/components/UpgradeModal';
import ErrorBoundary from '@/components/ErrorBoundary';
import { UserCard } from '@/components/UserCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Discover = () => {
  const { token, user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [showHostsOnly, setShowHostsOnly] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalType, setUpgradeModalType] = useState('connections');
  const [isAdminContact, setIsAdminContact] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
    fetchRecommendations();
    // Check if user is an admin contact (can call admin without premium)
    axios.get(`${API}/users/admin-contact-status`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setIsAdminContact(res.data.is_admin_contact)).catch(() => {});
  }, []);

  useEffect(() => {
    // Debounce search to avoid too many API calls
    const timer = setTimeout(() => {
      if (searchTerm || locationFilter || emailFilter || showHostsOnly) {
        fetchUsers();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, locationFilter, emailFilter, showHostsOnly]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      let url = `${API}/users/discover?`;
      const params = new URLSearchParams();
      if (searchTerm) params.append('name', searchTerm);
      if (locationFilter) params.append('location', locationFilter);
      if (emailFilter) params.append('email', emailFilter);
      
      // If showing hosts only, use different endpoint
      if (showHostsOnly && currentUser?.is_premium) {
        url = `${API}/hosting/available-hosts?`;
        if (locationFilter) params.append('location', locationFilter);
      } else {
        url = `${API}/users/discover?`;
      }
      
      const response = await axios.get(`${url}${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      if (showHostsOnly && error.response?.status === 403) {
        toast.error('Couch Surfing is a premium feature');
        setShowHostsOnly(false);
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/recommendations/matches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecommendations(response.data);
    } catch (error) {
      console.error('Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  const sendConnectionRequest = async (userId) => {
    try {
      await axios.post(
        `${API}/connections/request`,
        { target_user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Connection request sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-peach-50 to-teal-50">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 py-8 pt-24">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Discover People</h1>
          <p className="text-gray-600 text-lg">Find travelers and locals around the world</p>
        </div>

        {/* Search */}
        <div className="glass-dark rounded-2xl p-4 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                data-testid="search-users-input"
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && fetchUsers()}
                className="pl-12 py-6 rounded-xl border-gray-200"
              />
            </div>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                data-testid="email-filter-input"
                placeholder="Search by email..."
                value={emailFilter}
                onChange={(e) => setEmailFilter(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && fetchUsers()}
                className="pl-12 py-6 rounded-xl border-gray-200"
              />
            </div>
            <div className="relative">
              <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                data-testid="location-filter-input"
                placeholder="Filter by location..."
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && fetchUsers()}
                className="pl-12 py-6 rounded-xl border-gray-200"
              />
            </div>
            <Button
              data-testid="search-button"
              onClick={fetchUsers}
              className="bg-white text-black border-2 border-teal hover:bg-teal hover:text-white rounded-xl font-semibold py-6"
            >
              <Search className="w-5 h-5 mr-2" />
              Search
            </Button>
          </div>
          {/* Results count and Host filter */}
          <div className="mt-4 flex items-center justify-between">
            <span className="text-gray-700 font-semibold text-lg">
              {!loading && (searchTerm || emailFilter || locationFilter) && users.length > 0 && (
                <span className="bg-teal text-white px-3 py-1 rounded-full text-sm">
                  {users.length} {users.length === 1 ? 'user' : 'users'} found
                </span>
              )}
              {!loading && !searchTerm && !emailFilter && !locationFilter && (
                <span>{users.length} {users.length === 1 ? 'user' : 'users'}</span>
              )}
            </span>
            {currentUser?.is_premium && (
              <div className="flex items-center space-x-2">
                <Sofa className="w-5 h-5 text-teal" />
                <Label htmlFor="host-filter" className="text-sm font-medium cursor-pointer">
                  Available Hosts Only
                </Label>
                <Switch
                  id="host-filter"
                  checked={showHostsOnly}
                  onCheckedChange={setShowHostsOnly}
                />
              </div>
            )}
          </div>
        </div>

        {/* Search Results - Show at top when searching */}
        {(searchTerm || emailFilter || locationFilter) && users.length > 0 && !loading && (
          <div className="mb-8">
            <div className="flex items-center space-x-2 mb-4">
              <Search className="w-6 h-6 text-teal" />
              <h2 className="text-2xl font-bold">Search Results</h2>
              <Badge className="bg-teal text-white font-semibold">
                {users.length} found
              </Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="search-results">
              {users.map((user) => (
                <ErrorBoundary key={user.id}>
                  <UserCard
                    user={user}
                    onConnect={sendConnectionRequest}
                    navigate={navigate}
                    isSearchResult
                    currentUserPremium={currentUser?.is_premium || isAdminContact}
                  />
                </ErrorBoundary>
              ))}
            </div>
          </div>
        )}

        {/* AI Recommendations */}
        {recommendations.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center space-x-2 mb-4">
              <Sparkles className="w-6 h-6 text-coral fill-coral" />
              <h2 className="text-2xl font-bold">AI Recommended For You</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="recommendations-section">
              {recommendations.map((user) => (
                <ErrorBoundary key={user.id}>
                  <UserCard
                    user={user}
                    onConnect={sendConnectionRequest}
                    navigate={navigate}
                    isRecommended
                    currentUserPremium={currentUser?.is_premium || isAdminContact}
                  />
                </ErrorBoundary>
              ))}
            </div>
          </div>
        )}

        {/* All Users - Hide when showing search results */}
        {!(searchTerm || emailFilter || locationFilter) && (
          <div>
            <h2 className="text-2xl font-bold mb-4">All Users</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="users-grid">
              {loading ? (
                <div className="col-span-full glass rounded-3xl p-12 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral mx-auto mb-4"></div>
                  <p className="text-gray-500 text-lg">Searching...</p>
                </div>
              ) : users.length === 0 ? (
                <div className="col-span-full glass rounded-3xl p-12 text-center">
                  <p className="text-gray-500 text-lg">No users found</p>
                </div>
              ) : (
                users.map((user) => (
                  <ErrorBoundary key={user.id}>
                    <UserCard
                      user={user}
                      onConnect={sendConnectionRequest}
                      navigate={navigate}
                      currentUserPremium={currentUser?.is_premium || isAdminContact}
                    />
                  </ErrorBoundary>
                ))
              )}
            </div>
          </div>
        )}

        {/* No results message when searching */}
        {(searchTerm || emailFilter || locationFilter) && users.length === 0 && !loading && (
          <div className="glass rounded-3xl p-12 text-center">
            <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No users found matching your search</p>
            <p className="text-gray-400 text-sm mt-2">Try adjusting your search filters</p>
          </div>
        )}
      </div>
      
      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)}
        limitType={upgradeModalType}
      />
    </div>
  );
};

export default Discover;